import MediaModal from "@/components/shared/mediaModal";
import AddUploader from "@/components/shared/photoRegisterV2/addUploader";
import UploadErrorModal from "@/components/shared/photoRegisterV2/modals/errorModal";
import FileExtErrorModal from "@/components/shared/photoRegisterV2/modals/fileExtErrorModal";
import LoadingTipsModal from "@/components/shared/photoRegisterV2/modals/loadingTipsModal";
import Uploader from "@/components/shared/photoRegisterV2/uploader";
import { SETTINGS } from "@/constants/common/settings";
import { useZendesk } from "@/hooks/common/contact/useZendesk";
import {
  Variant,
  usePhotoRegister
} from "@/hooks/common/photo/usePhotoRegister";
import { usePhotoSort } from "@/hooks/common/photo/usePhotoSort";
import { usePhotoUploadAccept } from "@/hooks/common/photo/usePhotoUploadAccept";
import { addCrewReportPhotos } from "@/services/common/db/crew/reportPhotos";
import PhotoRegisterProcess from "@/services/common/photo/register";
import { taskDataAtom } from "@/states/crew/taskDataAtom";
import { PhotosAtom } from "@/types/common/photos";
import { DragDropContext, Droppable } from "@hello-pangea/dnd";
import { useAtomValue } from "jotai";
import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo
} from "react";
import OverLimitModal from "./modals/overLimitModal";

//MEMO: 既存の型を拡張
export type UploadPhotos = {
  id: number;
  path: string; //zendeskアップロード時はレスポンスのurlを入れる
  previewUrl: string;
  captured_at: string;
  is_aspect_ratio_valid: boolean;
  bucket?: string;
  user_id?: number;
  position?: number;
  token?: string; //Zendeskアップロード時に使用
  compressedFile?: File; //圧縮後のファイル
  lastModified?: number; //ファイル生成日時
};

type Props = {
  variant: Variant;
  defaultDisplayCount: number; //初期表示枚数
  defaultPhotoList?: PhotosAtom[]; //初期表示写真リスト
  targetId?: number;
  problemId?: number | null;
  maxUploadCount?: number; //アップロードできる写真の最大枚数
  updatePhotos?: PhotosAtom[]; //回転後の写真リストなどを想定（表示更新用）
  setPhotos?: React.Dispatch<React.SetStateAction<PhotosAtom[]>>; //写真リストを親コンポーネントに渡す（現段階ではphotosAtomへ渡す→今後globalには保持しないようにしたい）
  isRequired?: boolean; //写真アップが必須かどうか
  isError?: boolean; //エラー表示
  isOnErrorReload?: boolean; //エラー時に再読み込みボタンを表示
};

/* 写真アップロードv2 */
const PhotoRegisterV2: React.FC<Props> = ({
  variant,
  defaultDisplayCount,
  defaultPhotoList,
  targetId,
  problemId,
  maxUploadCount,
  updatePhotos,
  setPhotos,
  isRequired,
  isError,
  isOnErrorReload
}) => {
  const taskData = useAtomValue(taskDataAtom);
  const [photoList, setPhotoList] = useState<UploadPhotos[]>([]); //写真リスト
  const [photoCount, setPhotoCount] = useState<number>(0); //表示中の写真枚数
  const [maxPhotoCount, setMaxPhotoCount] = useState<number>(0); //アップロード可能な最大枚数
  const [inputKey, setInputKey] = useState(Date.now()); //エラーが発生した場合にinputをリセットするためのkey
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false); //エラーモーダル表示
  const [showOverLimitModal, setShowOverLimitModal] = useState<boolean>(false); //1回あたり20枚超過エラーモーダル表示
  const [showPhotoExpandModal, setShowPhotoExpandModal] = useState(false); //写真拡大表示モーダル
  const [slideIndex, setSlideIndex] = useState<number>(0); //スライドのインデックス
  const [showFileExtErrorModal, setShowFileExtErrorModal] = useState(false); //写真がpng,jpg,jpeg以外の場合のエラーモーダル表示
  const [errFileExt, setErrFileExt] = useState("");
  const [isPhotoSelectLoading, setIsPhotoSelectLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { targetAccept } = usePhotoUploadAccept();

  //Zendeskアップロード処理
  const {
    zendeskUploadResponse,
    setZendeskUploadData,
    setZendeskDeleteTarget,
    setZendeskUploadError,
    zendeskUploadError
  } = useZendesk({ photoList });

  //写真アップロード処理
  const {
    deleteTargetId,
    isMutating,
    showLoadingTipsModal,
    uploadPhotoResponse,
    uploadPhotoError,
    errStep,
    isDeleteComplete,
    delResponse,
    setReqType,
    setProblemId,
    setTargetId,
    setPhotoUploadFiles,
    setDeleteTargetId,
    setShowLoadingTipsModal,
    setIsDeleteComplete,
    createBlankPhoto,
    createDisplayPhotoList,
    convertPhotoList
  } = usePhotoRegister({
    photoList,
    defaultDisplayCount,
    variant
  });

  //写真ソート処理
  const { sortPhotoList, setSortResult, setSortProblemId, setSortTargetId } =
    usePhotoSort({
      variant,
      photoList
    });
  const isContact = variant === "contact"; //問い合わせフォームか否か

  // input要素のリセット処理
  const resetFileInputKey = useCallback(() => {
    setInputKey(Date.now());
    if (inputRef.current) {
      inputRef.current.value = ""; // input要素をリセット
    }
  }, []);

  //圧縮 -> 写真プレビュー用のURL生成 -> 表示中の写真枚数を更新 -> 写真をアップロード
  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;

      if (!files || files.length === 0) {
        resetFileInputKey(); // ファイル未選択時もリセット
        return;
      }

      try {
        // ファイルの拡張子を取得
        setIsPhotoSelectLoading(true); //写真選択時のローディングをset
        const fileExtList: string[] = [];
        for (const file of files) {
          const fileExtension = file.name.split(".").pop();
          const fileExt = fileExtension?.toLowerCase() || "";
          // ファイルの拡張子がpng,jpg,jpeg以外の場合はエラーを表示
          if (!SETTINGS.PHOTO.ACCEPT_FILE_TYPES_EXT.includes(fileExt)) {
            fileExtList.push(fileExt);
          }
        }
        if (fileExtList.length > 0) {
          setErrFileExt(fileExtList.join(", "));
          setShowFileExtErrorModal(true);
          resetFileInputKey();
          return;
        }

        const register = new PhotoRegisterProcess();
        const { updatedPhotoList, urls, compressedFiles } =
          await register.handlePhotoSelect(
            files,
            photoList,
            defaultDisplayCount,
            setPhotoList
          );

        //撮影日時データをセット
        if (taskData?.id) {
          await addCrewReportPhotos({
            taskId: taskData.id,
            photos: updatedPhotoList.map((photo) => ({
              photoId: photo.id,
              lastModified: photo.lastModified || 0
            }))
          });
        }
        //表示中の写真枚数を更新
        setPhotoCount(updatedPhotoList.length);

        //写真をアップロード
        if (isContact) {
          // Zendeskへ
          setZendeskUploadData({
            fileType: "photo",
            localUrl: urls,
            file: files
          });
        } else {
          //通常
          if (files.length > SETTINGS.PHOTO.MAX_MULTI_UPLOAD_COUNT) {
            setShowOverLimitModal(true);
          } else {
            setReqType("upload");
            setPhotoUploadFiles(compressedFiles); //圧縮後のファイルをセット -> アップロード処理へ
          }
        }
      } catch (error) {
        console.error("Error during file processing:", error);
      } finally {
        resetFileInputKey();
        setIsPhotoSelectLoading(false);
      }
    },
    [
      taskData,
      photoList,
      defaultDisplayCount,
      isContact,
      setPhotoList,
      setPhotoCount,
      setErrFileExt,
      setShowFileExtErrorModal,
      setZendeskUploadData,
      setShowOverLimitModal,
      setReqType,
      setPhotoUploadFiles
    ]
  );

  //写真アップローダーを追加する
  const handleAddUploader = useCallback(() => {
    if (photoCount < maxPhotoCount && photoCount > defaultDisplayCount) {
      setPhotoCount((prev) => prev + 1);
    }
    //写真選択画面を開く
    inputRef.current?.click();
  }, [photoCount, maxPhotoCount, defaultDisplayCount, setPhotoCount]);

  //写真アップロード完了時の処理（zendesk）
  useEffect(() => {
    if (zendeskUploadResponse) {
      //アップ済み写真をセット -> プレビュー表示
      const response = zendeskUploadResponse;
      const resPhotoCount = response?.length || 0;

      if (resPhotoCount < defaultDisplayCount) {
        //デフォの表示枚数に達していない場合は追加
        if (response) {
          setPhotoList(
            response.concat(
              createBlankPhoto(defaultDisplayCount - resPhotoCount)
            )
          );
        }
        setPhotoCount(defaultDisplayCount);
      } else {
        setPhotoList(response || []);
        setPhotoCount(resPhotoCount);
      }
    }

    //写真アップロードエラー時の処理
    if (zendeskUploadError) {
      setShowErrorModal(true);
      setZendeskUploadError(null);
    }
    //写真アップフォームで再度写真を選択できるようにkeyを更新
    resetFileInputKey();
  }, [zendeskUploadResponse, zendeskUploadError, defaultDisplayCount]);

  //写真アップロード完了時の処理（zendesk以外）
  useEffect(() => {
    if (uploadPhotoResponse && uploadPhotoResponse.length > 0 && targetId) {
      const response = createDisplayPhotoList(
        targetId,
        null,
        uploadPhotoResponse
      ).filter((photo) => photo.path);

      setPhotoList(response || []);
      setPhotoCount(response.length);
      resetFileInputKey();

      //親コンポーネントへ写真リストを渡す
      if (setPhotos) {
        if (response.length > 0) {
          //MEMO: v2をその他報告などへ展開する場合はここを変更(contextへのセットロジックを追加する事になる)
          setPhotos((prev) => [
            ...prev.filter((photo) => photo.id !== targetId), //MEMO: 異常箇所などだとproblemIdを使う事になる
            {
              id: targetId || 0,
              photos: response.map((photo, photoIndex) => ({
                ...photo,
                bucket: photo.path,
                position: photoIndex + 1
              }))
            }
          ]);
        } else {
          setPhotos([]);
        }
      }
    }
    if (uploadPhotoError) setShowErrorModal(true);
  }, [uploadPhotoResponse, uploadPhotoError, targetId]);

  //初期表示枚数分の空写真を生成
  useEffect(() => {
    if (photoList.length === 0) {
      if (defaultPhotoList) {
        const result = createDisplayPhotoList(targetId, defaultPhotoList, null);
        setPhotoList(result);
        setPhotoCount(result.length);
      } else {
        //アップ済みがない or お問い合わせフォームの場合
        setPhotoList(createBlankPhoto(defaultDisplayCount));
        setPhotoCount(defaultDisplayCount);
      }
      setMaxPhotoCount(maxUploadCount || SETTINGS.PHOTO.MAX_UOLOAD_COUNT); //最大アップロード枚数：デフォルト50枚
    }
    //各IDをセット
    setProblemId(problemId || null);
    setSortProblemId(problemId || null);
    setTargetId(targetId || null);
    setSortTargetId(targetId || null);
  }, [
    defaultPhotoList,
    defaultDisplayCount,
    maxUploadCount,
    problemId,
    targetId,
    photoList
  ]);

  //写真データ更新後の処理
  useEffect(() => {
    if (updatePhotos && targetId) {
      const result = createDisplayPhotoList(targetId, updatePhotos, null);
      setPhotoList(result);
      setPhotoCount(result.length);
    }
  }, [updatePhotos, targetId]);

  //写真ソート後の処理
  useEffect(() => {
    if (sortPhotoList.length > 0) setPhotoList(sortPhotoList);
  }, [sortPhotoList]);

  //写真削除後の処理
  useEffect(() => {
    if (isDeleteComplete && deleteTargetId && targetId) {
      // 削除された写真を除外し、positionを更新した新しいリストを作成
      const updatedPhotoList = photoList
        .filter((photo) => photo.id !== deleteTargetId && photo.id > 0)
        .map(
          (photo) => delResponse?.find((res) => res.id === photo.id) || photo
        );
      // 削除対象写真とidが0のデータを除外
      const result = createDisplayPhotoList(
        targetId,
        null,
        updatedPhotoList
      ).filter((photo) => photo.id > 0 && photo.path);

      // 親コンポーネントへ写真リストを渡す
      if (setPhotos) {
        setPhotos((prev) => [
          ...prev.filter((photo) => photo.id !== targetId),
          {
            id: targetId,
            photos: convertPhotoList(result)
          }
        ]);
      }

      setPhotoList(result);
      setIsDeleteComplete(false);
      setDeleteTargetId(null);
    }
  }, [isDeleteComplete, delResponse, deleteTargetId, photoList, targetId]);

  const droppableId = useMemo(() => `photoUploader-${targetId}`, [targetId]);

  return (
    <>
      <div>
        <DragDropContext onDragEnd={(r) => setSortResult(r)}>
          <Droppable droppableId={droppableId} direction="horizontal">
            {/* 写真アップローダー */}
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className="flex items-center gap-8 overflow-x-auto overflow-y-hidden"
              >
                {photoList.map((photo, index) => {
                  const responseUrl = photo.path; //アップロード済み写真のURL
                  const isUpLoading = !!(photo.previewUrl && photo.path === ""); //アップロード中かどうか
                  return (
                    <React.Fragment key={`uploader-${photo.id}-${index}`}>
                      <Uploader
                        index={index}
                        photoId={
                          isContact
                            ? photo.token || String(index)
                            : String(photo.id)
                        }
                        previewUrl={photo.previewUrl}
                        responseUrl={responseUrl}
                        deleteTargetId={deleteTargetId}
                        setReqType={setReqType}
                        setSlideIndex={setSlideIndex}
                        setShowPhotoExpandModal={setShowPhotoExpandModal}
                        setDeleteTarget={setZendeskDeleteTarget}
                        setDeleteTargetId={
                          isContact ? undefined : setDeleteTargetId
                        }
                        isError={isError}
                        isRequired={isRequired}
                        isLoading={isMutating}
                        isUpLoading={isUpLoading}
                        isReadyLoading={isPhotoSelectLoading}
                        isAspectRatioValid={photo.is_aspect_ratio_valid}
                        handleAddUploader={handleAddUploader}
                        capturedAt={photo.captured_at}
                      />
                    </React.Fragment>
                  );
                })}
                {provided.placeholder}

                {/* さらに追加ボタン */}
                {photoCount < maxPhotoCount && (
                  <AddUploader
                    isUpLoading={isMutating}
                    handleAddUploader={handleAddUploader}
                  />
                )}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        {/* input要素 */}
        <input
          key={`photo-upload-${problemId || targetId}-${inputKey}`}
          ref={inputRef}
          type="file"
          accept={targetAccept}
          className="hidden"
          multiple
          onChange={handleFileChange}
        />
      </div>

      {/* -- モーダル群 -- */}
      <OverLimitModal
        showModal={showOverLimitModal}
        setShowModal={setShowOverLimitModal}
      />
      {/* アップロードエラー */}
      <UploadErrorModal
        errStep={errStep}
        error={zendeskUploadError || uploadPhotoError}
        showErrorModal={showErrorModal}
        setShowErrorModal={setShowErrorModal}
        isReload={isOnErrorReload}
      />
      {/* アップロード時間について */}
      <LoadingTipsModal
        showModal={showLoadingTipsModal && !uploadPhotoError}
        setShowModal={setShowLoadingTipsModal}
      />
      {/* 写真拡大表示 */}
      <MediaModal
        media={convertPhotoList(photoList)}
        show={showPhotoExpandModal}
        setShow={setShowPhotoExpandModal}
        canRotate={true}
        mediaIndex={slideIndex}
        setMediaIndex={setSlideIndex}
        isZendesk={isContact}
      />
      {/* 写真がpng,jpg,jpeg以外の場合のエラー表示 */}
      <FileExtErrorModal
        errFileExt={errFileExt}
        showErrorModal={showFileExtErrorModal}
        setShowErrorModal={setShowFileExtErrorModal}
      />
    </>
  );
};

export default React.memo(PhotoRegisterV2);
